/**
 * AGRUPAR Y FILTRAR EL CARTAPACIO — la regla, sin React.
 *
 * ## La categoría deja de ser un control de la fila y pasa a ser la ESTRUCTURA
 *
 * Antes cada fila llevaba su propio `Select` de categoría: 168 px fijos por documento, el
 * bloque que desbordaba el panel, y veinte combos de Radix montados para leer veinte
 * palabras. Ahora la categoría es el ENCABEZADO del grupo — que además es lo que el ADR 0022
 * ya decía («la categoría ES una carpeta raíz»): un curador ve la clasificación de veinte
 * documentos en tres renglones en lugar de veinte controles, y reclasificar uno sigue estando
 * a un click, dentro de su menú «⋯».
 *
 * ## Qué grupos existen y en qué orden
 *
 * Los de la plantilla, en el `order` que fija la plantilla, y **sólo los que tienen
 * contenido**: una carpeta vacía en pantalla es ruido que hay que saltear en cada lectura.
 * Al final, siempre, «Sin categoría» — con ese nombre y no «Otros documentos», que es como lo
 * ve el CLIENTE en su app: acá el curador necesita leer que falta clasificarlos, no el rótulo
 * con el que van a salir.
 *
 * Un documento cuya categoría ya no está en la plantilla —una deprecada que el selector no
 * ofrece— **no desaparece**: cae en su propio grupo con el id por nombre. Esconderlo sería
 * perderlo, y es justo el contenido que alguien tiene que mover.
 *
 * ## El filtro cuenta sobre lo FILTRADO
 *
 * El número del encabezado es el de lo que se ve. Un «Planos 12» sobre tres filas visibles
 * obliga a preguntarse dónde están las otras nueve, que es exactamente la duda que un
 * buscador tiene que borrar.
 */

/** Lo mínimo que este módulo necesita saber de un documento. Ni React ni el DTO entero. */
export type GroupableDocument = {
  id: string
  name: string
  categoryId: string | null
  visibility: 'public' | 'internal'
}

export type DocumentCategoryRef = {
  id: string
  name: string
}

/** Por qué estado se filtra. `all` es el default y no esconde nada. */
export const DOCUMENT_STATE_FILTER = {
  all: 'all',
  public: 'public',
  internal: 'internal'
} as const

export type DocumentStateFilter = (typeof DOCUMENT_STATE_FILTER)[keyof typeof DOCUMENT_STATE_FILTER]

/**
 * El id sintético del grupo de los que no tienen categoría. No es un `cat_` y no puede serlo.
 *
 * Interno: quien consume no tiene por qué compararlo. El grupo ya declara `uncategorized`, y dos
 * formas de preguntar lo mismo es una de más — la que se olvide de actualizarse pinta el grupo
 * equivocado sin fallar.
 */
const UNCATEGORIZED_GROUP_ID = '__uncategorized__'

export type DocumentGroup<TDocument extends GroupableDocument> = {
  /** El `cat_` de la plantilla, o `UNCATEGORIZED_GROUP_ID`. */
  id: string
  name: string
  documents: TDocument[]
  /** `true` en el grupo de los no clasificados: la UI lo distingue con forma, no con texto. */
  uncategorized: boolean
}

/**
 * Normaliza para comparar: sin mayúsculas y sin tildes.
 *
 * Buscar «plano» tiene que encontrar «PLANO» y «Plano Arquitectónico», y buscar «arquitect»
 * tiene que encontrar «Arquitectónico». Los nombres reales de este catálogo son de asesores
 * escribiendo a mano, la mitad en mayúsculas: un filtro sensible a tildes no encuentra nada y
 * quien busca concluye que el documento no está.
 */
function normalize(value: string): string {
  return value
    .toLocaleLowerCase('es')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

/** Ordena por nombre con criterio humano: «Plano 2» antes que «Plano 10». */
function byName(left: { name: string }, right: { name: string }): number {
  return left.name.localeCompare(right.name, 'es', { numeric: true, sensitivity: 'base' })
}

export type GroupDocumentsInput<TDocument extends GroupableDocument> = {
  documents: TDocument[]
  /** La plantilla global YA sin deprecadas y en su `order`. Define qué grupos hay y su orden. */
  categories: DocumentCategoryRef[]
  /** Texto libre del buscador. Vacío = no filtra. */
  search?: string
  state?: DocumentStateFilter
}

/**
 * ¿Este documento pasa el filtro?
 *
 * Interno a propósito: la pantalla siempre pregunta por el AGRUPADO, nunca por un documento
 * suelto, y exportarlo sería ofrecer un segundo camino para responder la misma pregunta — el
 * día que los dos se separen, la lista y su conteo dejan de coincidir.
 */
function matchesFilters(
  document: GroupableDocument,
  search: string,
  state: DocumentStateFilter
): boolean {
  if (state !== DOCUMENT_STATE_FILTER.all && document.visibility !== state) return false
  const needle = normalize(search.trim())
  if (needle.length === 0) return true
  return normalize(document.name).includes(needle)
}

/**
 * Agrupa por categoría, filtra, y devuelve **sólo los grupos con contenido**.
 *
 * El orden de los grupos es el de la plantilla; el de las filas dentro de cada grupo, el del
 * nombre. Alfabético y no por fecha a propósito: una carpeta se abre para ENCONTRAR algo, y
 * «lo último que subí» ya está arriba de todo en la cabeza de quien lo subió.
 */
export function groupDocuments<TDocument extends GroupableDocument>({
  documents,
  categories,
  search = '',
  state = DOCUMENT_STATE_FILTER.all
}: GroupDocumentsInput<TDocument>): DocumentGroup<TDocument>[] {
  const visible = documents.filter((document) => matchesFilters(document, search, state))

  const byCategory = new Map<string, TDocument[]>()
  for (const document of visible) {
    const key = document.categoryId ?? UNCATEGORIZED_GROUP_ID
    const bucket = byCategory.get(key)
    if (bucket) bucket.push(document)
    else byCategory.set(key, [document])
  }

  const groups: DocumentGroup<TDocument>[] = []

  for (const category of categories) {
    const bucket = byCategory.get(category.id)
    if (!bucket || bucket.length === 0) continue
    groups.push({
      id: category.id,
      name: category.name,
      documents: [...bucket].sort(byName),
      uncategorized: false
    })
    byCategory.delete(category.id)
  }

  // Lo que quedó y NO es el grupo de sin clasificar son categorías que la plantilla ya no
  // ofrece —deprecadas—. Siguen apareciendo porque tienen contenido que alguien debe mover;
  // esconderlas sería perder el archivo sin avisar.
  const orphanIds = [...byCategory.keys()].filter((key) => key !== UNCATEGORIZED_GROUP_ID).sort()
  for (const orphanId of orphanIds) {
    const bucket = byCategory.get(orphanId) ?? []
    groups.push({
      id: orphanId,
      name: 'Categoría dada de baja',
      documents: [...bucket].sort(byName),
      uncategorized: false
    })
  }

  const loose = byCategory.get(UNCATEGORIZED_GROUP_ID)
  if (loose && loose.length > 0) {
    groups.push({
      id: UNCATEGORIZED_GROUP_ID,
      name: 'Sin categoría',
      documents: [...loose].sort(byName),
      uncategorized: true
    })
  }

  return groups
}
